// Not the package root: that installs the Luxon zone override as a side effect.
import { getTimezoneOffset } from "@offstreetparking/date-and-time/timezone-data";
import { isValid, parseISO } from "date-fns";

// No format may set timeZoneName: instants are shifted by the target zone's offset and then
// formatted in UTC, so the name would always render as UTC.
const FORMAT_OPTIONS: Record<FormatDateProps["formatType"], Intl.DateTimeFormatOptions> = {
	// Jun 15, 2023, 1:30 PM
	Abbreviated_DateTime: {
		year: "numeric",
		month: "short",
		day: "numeric",
		hour: "numeric",
		minute: "2-digit",
	},
	// Jun 15, 2023, 1:30 PM
	Abbreviated_DateTime_No_Year: {
		month: "short",
		day: "numeric",
		hour: "numeric",
		minute: "2-digit",
	},
	// Jun 15, 2023
	Abbreviated_Date: {
		year: "numeric",
		month: "short",
		day: "numeric",
	},
	// Jun 15
	Abbreviated_Date_No_Year: {
		month: "short",
		day: "numeric",
	},
	// June 15, 2023
	Long_Date: {
		year: "numeric",
		month: "long",
		day: "numeric",
	},
	// June 2023
	Long_Month: {
		year: "numeric",
		month: "long",
	},
	// Wednesday
	Weekday: {
		weekday: "long",
	},
	// Wed, Jun 15
	Short_Weekday_Abbreviated_Date_No_Year: {
		weekday: "short",
		month: "short",
		day: "numeric",
	},
	// Wednesday, Jun 15, 2023
	Weekday_Abbreviated_Date: {
		weekday: "long",
		year: "numeric",
		month: "short",
		day: "numeric",
	},
	// Wednesday, June 15, 2023
	Weekday_Long_Date: {
		weekday: "long",
		year: "numeric",
		month: "long",
		day: "numeric",
	},
	// 9:02 AM
	Time: {
		hour: "numeric",
		minute: "2-digit",
	},
	// 8 AM - 5 PM (or 8:30 AM - 5:45 PM if minutes present)
	// Note: This format has custom logic in formatDateRange
	Abbreviated_Time: {
		hour: "numeric",
	},
};

export type FormatDateProps = {
	date: Date | string | undefined;
	formatType:
		| "Abbreviated_DateTime"
		| "Abbreviated_DateTime_No_Year"
		| "Abbreviated_Date"
		| "Abbreviated_Date_No_Year"
		| "Long_Date"
		| "Long_Month"
		| "Weekday"
		| "Short_Weekday_Abbreviated_Date_No_Year"
		| "Weekday_Abbreviated_Date"
		| "Weekday_Long_Date"
		| "Time"
		| "Abbreviated_Time";
	timezone?: string | null;
	language?: string;
};

type PreparedDate = {
	date: Date;
	timeZone: string | undefined;
};

let cachedHostZone: string | undefined;
// Not a no-op: naming the zone routes it through our own timezone data, while leaving it undefined
// lets the browser resolve the reader's zone with its own rules.
const hostZone = () => {
	cachedHostZone ??= new Intl.DateTimeFormat().resolvedOptions().timeZone;
	return cachedHostZone;
};

const prepareDate = ({ date, timezone }: { date: Date; timezone: string | null }): PreparedDate => {
	const zoneName = timezone || hostZone();
	const offsetMinutes = getTimezoneOffset({ instant: date, zoneName });
	if (typeof offsetMinutes === "undefined") {
		return { date, timeZone: zoneName };
	}

	return {
		date: new Date(date.getTime() + offsetMinutes * 60_000),
		timeZone: "UTC",
	};
};

const getMinutes = ({ date, timeZone }: PreparedDate) => {
	if (timeZone === "UTC") {
		return date.getUTCMinutes();
	}
	if (typeof timeZone === "undefined") {
		return date.getMinutes();
	}

	const minutePart = new Intl.DateTimeFormat("en-US-u-nu-latn", {
		minute: "numeric",
		timeZone,
	})
		.formatToParts(date)
		.find((part) => part.type === "minute");

	return Number(minutePart?.value);
};

const getOptions = ({
	formatType,
	timeZone,
}: {
	formatType: FormatDateProps["formatType"];
	timeZone: string | undefined;
}): Intl.DateTimeFormatOptions => ({
	...FORMAT_OPTIONS[formatType],
	timeZone,
});

export const formatDate = ({
	date,
	formatType,
	timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
	language = "en",
}: FormatDateProps): string | "Invalid Date" => {
	if (typeof date === "undefined") {
		return "-";
	}

	const dateTime = isValid(date) ? (date as Date) : typeof date === "string" ? parseISO(date) : new Date(date);
	if (!isValid(dateTime) || !FORMAT_OPTIONS[formatType]) {
		return "Invalid Date";
	}

	const preparedDate = prepareDate({ date: dateTime, timezone });

	if (formatType === "Abbreviated_Time") {
		const formatter = new Intl.DateTimeFormat(language, {
			hour: "numeric",
			minute: getMinutes(preparedDate) === 0 ? undefined : "2-digit",
			timeZone: preparedDate.timeZone,
		});
		return formatter.format(preparedDate.date);
	}

	const dateTimeFormat = new Intl.DateTimeFormat(
		language,
		getOptions({ formatType, timeZone: preparedDate.timeZone }),
	);

	return dateTimeFormat.format(preparedDate.date);
};

export type FormatDateRangeProps = {
	startDate: Date | string | undefined;
	endDate: Date | string | undefined;
} & Omit<FormatDateProps, "date">;
export const formatDateRange = ({
	startDate: rawStartDate,
	endDate: rawEndDate,
	language = "en",
	formatType,
	timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
}: FormatDateRangeProps): string => {
	if (typeof rawStartDate === "undefined" || typeof rawEndDate === "undefined") {
		return "-";
	}
	const startDate = isValid(rawStartDate)
		? (rawStartDate as Date)
		: typeof rawStartDate === "string"
			? parseISO(rawStartDate)
			: new Date(rawStartDate);
	const endDate = isValid(rawEndDate)
		? (rawEndDate as Date)
		: typeof rawEndDate === "string"
			? parseISO(rawEndDate)
			: new Date(rawEndDate);

	if (!isValid(startDate) || !isValid(endDate) || !FORMAT_OPTIONS[formatType]) {
		return "Invalid Date";
	}

	const preparedStartDate = prepareDate({ date: startDate, timezone });
	const preparedEndDate = prepareDate({ date: endDate, timezone });
	const dateTimeFormat = new Intl.DateTimeFormat(
		language,
		getOptions({ formatType, timeZone: preparedStartDate.timeZone }),
	);

	// A fall-back transition runs the same clock hour twice, so a range can legitimately end at an
	// earlier clock time than it started: 1:30 AM to 1:15 AM is a real 45 minutes. Intl's range
	// formatters reject that order, so format each endpoint on its own.
	const isReversed = preparedEndDate.date < preparedStartDate.date;
	const reversedRange = () =>
		`${dateTimeFormat.format(preparedStartDate.date)}\u2009\u2013\u2009${dateTimeFormat.format(preparedEndDate.date)}`;

	if (formatType === "Time") {
		if (isReversed) {
			return reversedRange();
		}

		// To avoid displaying the dates when spanning
		// multiple days in the time format
		return dateTimeFormat
			.formatRangeToParts(preparedStartDate.date, preparedEndDate.date)
			.filter(
				(part) =>
					part.type !== "month" &&
					part.type !== "day" &&
					part.type !== "year" &&
					part.value !== "/" &&
					part.value !== ", ",
			)
			.map((part) => part.value)
			.join("");
	}

	if (formatType === "Abbreviated_Time") {
		// Format: "8 AM - 5 PM" if minutes are 0, otherwise "8:30 AM - 5:45 PM"
		const formatTime = (preparedDate: PreparedDate) => {
			const formatter = new Intl.DateTimeFormat(language, {
				hour: "numeric",
				minute: getMinutes(preparedDate) === 0 ? undefined : "2-digit",
				timeZone: preparedDate.timeZone,
			});
			return formatter.format(preparedDate.date);
		};

		return `${formatTime(preparedStartDate)} - ${formatTime(preparedEndDate)}`;
	}

	if (isReversed) {
		return reversedRange();
	}

	return dateTimeFormat.formatRange(preparedStartDate.date, preparedEndDate.date);
};
